// The steer routes (CONTRACT v1 §1). Owner-only and bound to the answer's
// resumable stream id, like resume and stop. Every outcome maps to what the
// waiting message does next; a network failure never loses the message.
import { apiUrl } from '@/lib/shell';

export type SteerPostOutcome = { kind: 'waiting' } | { kind: 'next' } | { kind: 'credits' };

export type SteerDeleteOutcome =
  | { kind: 'removed' }
  | { kind: 'read' }
  | { kind: 'gone' }
  | { kind: 'unconfirmed' };

type Fetch = typeof fetch;

export async function postSteer(
  fetcher: Fetch,
  headers: () => Record<string, string>,
  streamId: string,
  message: { id: string; threadId: string; text: string },
): Promise<SteerPostOutcome> {
  try {
    const response = await fetcher(apiUrl(`/api/chat/steer/${encodeURIComponent(streamId)}`), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...headers() },
      body: JSON.stringify(message),
    });
    if (response.status === 202 || response.ok) return { kind: 'waiting' };
    if (response.status === 402) return { kind: 'credits' };
    return { kind: 'next' };
  } catch {
    return { kind: 'next' };
  }
}

export async function deleteSteer(
  fetcher: Fetch,
  headers: () => Record<string, string>,
  streamId: string,
  id: string,
): Promise<SteerDeleteOutcome> {
  try {
    const response = await fetcher(
      apiUrl(`/api/chat/steer/${encodeURIComponent(streamId)}/${encodeURIComponent(id)}`),
      { method: 'DELETE', credentials: 'same-origin', headers: headers() },
    );
    if (response.status === 204 || response.ok) return { kind: 'removed' };
    if (response.status === 409) {
      const body = (await response.json().catch(() => null)) as { error?: unknown } | null;
      return body?.error === 'already_read' ? { kind: 'read' } : { kind: 'unconfirmed' };
    }
    return response.status === 404 ? { kind: 'gone' } : { kind: 'unconfirmed' };
  } catch {
    return { kind: 'unconfirmed' };
  }
}
